// pages/Devices/deviceedit.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  editDevice,
  getDeviceOffice,
  clearUpdateState,
  selectUpdateLoading,
  selectUpdateError,
  selectUpdateSuccess,
  selectDeviceOffice
} from '../../features/devices/deviceSlice';
import { officeAPI } from '../../features/offices/officesAPI';
import SearchableDealerSelect from './SearchableDealerSelect';
import Select from 'react-select';

const US_STATES = [
  "AL", "AK", "AS", "AZ", "AR", "CA", "CO", "CT", "DE", "DC",
  "FM", "FL", "GA", "GU", "HI", "ID", "IL", "IN", "IA", "KS",
  "KY", "LA", "ME", "MH", "MD", "MA", "MI", "MN", "MS", "MO",
  "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "MP",
  "OH", "OK", "OR", "PW", "PA", "PR", "RI", "SC", "SD", "TN",
  "TX", "UT", "VT", "VI", "VA", "WA", "WV", "WI", "WY",
  "INDIA", "INDIAV2", "INDIAV3","INDIAV4","CAV4","INDIAMT", "INDIAMTPL", "INDIASE", "BZ"
];

const DeviceEdit = ({ isOpen, onClose, device, dealers, isAdmin }) => {
  const dispatch = useDispatch();
  const updateLoading = useSelector(selectUpdateLoading);
  const updateError = useSelector(selectUpdateError);
  const updateSuccess = useSelector(selectUpdateSuccess);
  const deviceOffice = useSelector(selectDeviceOffice);
  const loggedInUser = useSelector((state) => state.auth.user);
  
  const [formData, setFormData] = useState({
    sno: '',
    snostate: '',
    dealerName: '',
    dealerId: '',
    officeId: '',
    devId: '',
    cbid: '',
    schipid: '',
    cchipid: '',
    status: 'NotAssigned',
    timezone: 'GMT',
    lastsentdate: 'A200',
    autotimeset: 1,
    calibrationdate: '',
    comment:'',
    lastiddownload: 0,
    lastoperation: '',
    lastopdate: '',
    iswifi: 0,
    cam4: ''
  });
  
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [offices, setOffices] = useState([]);
  const [officesLoading, setOfficesLoading] = useState(false);
  const [dealerSearch, setDealerSearch] = useState('');
  const [officeSearch, setOfficeSearch] = useState('');
  
  // Extract state from SNO when device changes
  useEffect(() => {
    if (device) {
      const sno = String(device.sno || '').trim().toUpperCase();
      const savedState = String(device.snostate || device.state || '').trim().toUpperCase();
      const snostate = US_STATES.includes(savedState)
        ? savedState
        : [...US_STATES].sort((a, b) => b.length - a.length)
          .find(state => sno.substring(3).startsWith(state)) || '';
      
      const matchingDealer = dealers?.find((dealer) =>
        dealer.dealerName === device.dealer || String(dealer.dealerId) === String(device.dealerId)
      );
      const initialDealerName = matchingDealer?.dealerName || device.dealerName || device.dealer || '';
      setDealerSearch(initialDealerName);

      setFormData({
        sno: device.sno || '',
        snostate: snostate,
        dealerName: initialDealerName,
        dealerId: device.dealerId || matchingDealer?.dealerId || '',
        officeId: '',
        devId: device.handsetId || '',
        cbid: device.deviceId || '',
        schipid: device.samplerchipid ?? device.schipid ?? '',
        cchipid: device.controlchipid ?? device.cchipid ?? '',
        status: device.status || 'NotAssigned',
        timezone: device.timezone || 'GMT',
        lastsentdate: device.lastsentdate || 'A200',
        autotimeset: device.autotimeset || 1,
        calibrationdate: device.calibrationDate || new Date().toLocaleString(),
        comment: device.comment || '',
        lastiddownload: device.lastiddownload || 0,
        lastoperation: device.lastOperation || '',
        lastopdate: device.lastOpDate || '',
        iswifi: device.iswifi || 0,
        cam4: device.cam4 || ''
      });
      
      if (device.sno) {
        dispatch(getDeviceOffice(device.sno));
      }
    }
    // Initialize only when a different device is opened. Redux updates while editing
    // must not overwrite a newly selected dealer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isOpen, device?.sno, dealers, dispatch]);
  useEffect(() => {
    if (deviceOffice) {
      setFormData(prev => ({ ...prev, officeId: String(deviceOffice) }));
      // const selectedOffice = offices.find(office => String(office.officeId || office.id) === String(deviceOffice));
      const selectedOffice = offices.find(
  office => String(office.officeId || office.id) === String(deviceOffice)
);

if (selectedOffice) {
  setOfficeSearch(selectedOffice.officeName || '');
}
      if (selectedOffice) setOfficeSearch(selectedOffice.officeName || '');
    }
  }, [deviceOffice, offices]);

  useEffect(() => {
    if (!isOpen) return;
    const dealerId = isAdmin ? formData.dealerId : loggedInUser?.dealerId;

    setOfficesLoading(true);
    const request = dealerId
      ? officeAPI.fetchDealerOffices(dealerId)
      : (isAdmin ? officeAPI.fetchOffices() : Promise.resolve([]));

    request
      .then((data) => setOffices(Array.isArray(data) ? data : []))
      .catch(() => setOffices([]))
      .finally(() => setOfficesLoading(false));
  }, [isOpen, isAdmin, formData.dealerId, loggedInUser?.dealerId]);
  
  // Handle success/error messages
  useEffect(() => {
    if (updateSuccess) {
      showAlert('success', 'Device updated successfully!');
      setTimeout(() => {
        onClose();
        dispatch(clearUpdateState());
      }, 1500);
    }
    
    if (updateError) {
      showAlert('error', updateError);
      dispatch(clearUpdateState());
    }
  }, [updateSuccess, updateError, dispatch, onClose]);
  
  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 3000);
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.snostate) newErrors.snostate = 'State is required';
    if (!formData.sno) newErrors.sno = 'SNO is required';
    if (isAdmin && !formData.dealerName) newErrors.dealerName = 'Dealer is required';
    if (!formData.devId) newErrors.devId = 'Device ID is required';
    if (!formData.cbid) newErrors.cbid = 'Control Board ID is required';
    if (!formData.status) newErrors.status = 'Status is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };
  
  const handleDealerChange = (dealerName) => {
    setDealerSearch(dealerName);
    const normalizedName = dealerName.trim().toLowerCase();
    const selectedDealer = dealers.find(d => String(d.dealerName || '').trim().toLowerCase() === normalizedName);
    if (selectedDealer) {
      setFormData(prev => ({
        ...prev,
        dealerName: selectedDealer.dealerName,
        dealerId: selectedDealer.dealerId,
        officeId: ''
      }));
      setOfficeSearch('');
      if (errors.dealerName) setErrors(prev => ({ ...prev, dealerName: '' }));
    } else {
      setFormData(prev => ({ ...prev, dealerName: '', dealerId: '', officeId: '' }));
    }
  };

  const handleOfficeChange = (officeName) => {
    setOfficeSearch(officeName);
    const selectedOffice = offices.find(office => office.officeName === officeName);
    handleInputChange('officeId', selectedOffice ? (selectedOffice.officeId || selectedOffice.id) : '');
  };
  
  // ✅ Create FormData exactly like Vue's loadFormData2 function
  const loadFormData = () => {
    const fd = new FormData();
    fd.append("sno", formData.sno);
    fd.append("devId", formData.devId);
    fd.append("cbid", formData.cbid);
    fd.append("schipid", formData.schipid);
    fd.append("cchipid", formData.cchipid);
    fd.append("dealer", formData.dealerId || loggedInUser?.dealerId || '');
    fd.append("calibrationdate", formData.calibrationdate);
    fd.append("comment", formData.comment);
    fd.append("status", formData.status);
    fd.append("lastiddownload", formData.lastiddownload);
    fd.append("lastsentdate", formData.lastsentdate);
    fd.append("iswifi", formData.iswifi);
    fd.append("autotimeset", formData.autotimeset);
    fd.append("timezone", formData.timezone);
    fd.append("lastoperation", formData.lastoperation);
    fd.append("lastopdate", formData.lastopdate);
    fd.append("cam4", formData.cam4);
    
    // Add office if available (matching Vue)
    if (formData.officeId) {
      fd.append("office", formData.officeId);
    }
    
    return fd;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Create FormData exactly like Vue component
    const devdata = loadFormData();
    
    // Call editDevice with id (sno) and devdata (FormData)
    // This matches Vue: editDevice({ id: this.editedItem.sno, devdata: fd })
    await dispatch(editDevice({ 
      id: formData.sno, 
      devdata: devdata 
    }));
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="edit-device-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Device</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        
        {alert.show && (
          <div className={`alert-banner ${alert.type}`}>
            <span>{alert.message}</span>
            <button className="alert-close" onClick={() => setAlert({ show: false })}>✕</button>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Select State <span className="required">*</span></label>
                                <div className='select-wrapper'>
                <Select
                  aria-label="Select State"
                  options={US_STATES.map(state => ({ value: state, label: state }))}
                  value={formData.snostate ? { value: formData.snostate, label: formData.snostate } : null}
                  onChange={option => handleInputChange('snostate', option?.value || '')}
                  className={errors.snostate ? 'error' : ''}
                  classNamePrefix="state-select"
                  placeholder="Search & select state..."
                  isSearchable
                  isDisabled={updateLoading}
                />
                </div>
                {errors.snostate && <span className="error-message">{errors.snostate}</span>}
              </div>
              
              <div className="form-group">
                <label>SNO <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.sno}
                    onChange={(e) => handleInputChange('sno', e.target.value)}
                  className={errors.sno ? 'error' : ''}
                />
                {errors.sno && <span className="error-message">{errors.sno}</span>}
              </div>
            </div>
            
            {isAdmin && (
              <div className="form-group">
                <label>Select Dealer <span className="required">*</span></label>
                <SearchableDealerSelect
                  dealers={dealers || []}
                  value={dealerSearch}
                  onSelect={handleDealerChange}
                  hasError={Boolean(errors.dealerName)}
                />
                {errors.dealerName && <span className="error-message">{errors.dealerName}</span>}
              </div>
            )}

            <div className="form-group">
              <label>Select Office</label>
              <SearchableDealerSelect
                dealers={offices}
                value={officeSearch}
                onSelect={handleOfficeChange}
                getLabel={(office) => office.officeName}
                getKey={(office) => office.officeId || office.id || office.officeName}
                disabled={officesLoading || (isAdmin && !formData.dealerId)}
                placeholder={officesLoading ? 'Loading officers...' : (isAdmin && !formData.dealerId ? 'Select a dealer first' : 'Search office')}
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Device ID <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.devId}
                  onChange={(e) => handleInputChange('devId', e.target.value)}
                  className={errors.devId ? 'error' : ''}
                  placeholder="Enter Device ID"
                />
                {errors.devId && <span className="error-message">{errors.devId}</span>}
              </div>
              
              <div className="form-group">
                <label>Control Board ID <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.cbid}
                  onChange={(e) => handleInputChange('cbid', e.target.value)}
                  className={errors.cbid ? 'error' : ''}
                  placeholder="Enter Control Board ID"
                />
                {errors.cbid && <span className="error-message">{errors.cbid}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Sampler Chip ID</label>
                <input
                  type="text"
                  value={formData.schipid}
                  onChange={(e) => handleInputChange('schipid', e.target.value)}
                  placeholder="Enter Sampler Chip ID"
                />
              </div>

              <div className="form-group">
                <label>CB Chip ID</label>
                <input
                  type="text"
                  value={formData.cchipid}
                  onChange={(e) => handleInputChange('cchipid', e.target.value)}
                  placeholder="Enter CB Chip ID"
                />
              </div>
            </div>

            <div className="form-group">
  <label>Ownership Type</label>

  <div className="select-wrapper">
    <select
      value={formData.cam4}
      onChange={(e) => handleInputChange('cam4', e.target.value)}
    >
      <option value="">Select Ownership Type</option>
      <option value="Rental">Rental</option>
      <option value="Purchased">Purchased</option>
      <option value="AMC">AMC</option>
    </select>

    <span className="status-down-arrow">&#9660;</span>
  </div>
</div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Device Mode <span className="required">*</span></label>
                                <div className='select-wrapper'>
                <select
                  value={formData.lastsentdate}
                  onChange={(e) => handleInputChange('lastsentdate', e.target.value)}
                >
                  <option value="A200">A200</option>
                  <option value="A300I">A300I</option>
                  <option value="A300U">A300U</option>
                  <option value="A300O">A300O</option>
                  <option value="A200U">A200U</option>
                   <option value="A200I">A200I</option>
                </select>
                  <span className="status-down-arrow">&#9660;</span>
                </div>
              </div>
              
              <div className="form-group">
                <label>Status <span className="required">*</span></label>
                                <div className='select-wrapper'>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className={errors.status ? 'error' : ''}
                >
                  <option value="Registered">Registered</option>
                  <option value="UnderTesting">Under Testing</option>
                  <option value="Testcompleted">Test Completed</option>
                  <option value="readytoinstall">Ready to install</option>
                  <option value="Installed">Installed</option>
                  <option value="Removed">Removed</option>
                  <option value="NotAssigned">NotAssigned</option>
                  <option value="Trashed">Trashed</option>
                </select>
                     <span className="status-down-arrow">&#9660;</span>
                </div>
                {errors.status && <span className="error-message">{errors.status}</span>}
              </div>
            </div>
            
            <div className="form-group">
              <label>Timezone</label>
                              <div className='select-wrapper'>
              <select
                value={formData.timezone}
                onChange={(e) => handleInputChange('timezone', e.target.value)}
              >
                <option value="GMT">GMT</option>
                <option value="America/Los_Angeles">America/Los_Angeles</option>
                <option value="Asia/Kolkata">Asia/Kolkata</option>
                <option value="Europe/London">Europe/London</option>
              </select>
   <span className="status-down-arrow">&#9660;</span>
              </div>
            </div>

            {/* <div className="form-group">
  <label>Comment</label>
  <textarea
    value={formData.comment}
    onChange={(e) => handleInputChange('comment', e.target.value)}
    placeholder="Enter comment"
    rows={3}
  />
</div> */}
            
            {/* {deviceOffice && (
              <div className="office-info">
                <label>Assigned Office/Client:</label>
                <span className="office-value">{deviceOffice}</span>
              </div>
            )} */}
          </div>
          
          <div className="modal-footer">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={updateLoading}>
              {updateLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeviceEdit;
